import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sidebar } from "../../components/sidebar";
import { Wrapper } from "../../components/wrapper";
import { createCustomer } from "../../services/customer";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Snackbar } from "../../components/snackbar";
import { DEFAULT_SNACK } from "../../utils/snacks";
import { DEFAULT_CUSTOMER } from "../../utils/states";

const formatRut = (value) => {
  const cleaned = value.replace(/[^0-9kK]/g, "");
  if (cleaned.length <= 1) return cleaned;

  const body = cleaned.slice(0, -1);
  const dv = cleaned.slice(-1);

  let formatted = body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${formatted}-${dv}`;
};

const formatPhone = (value) => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length === 0) return "";

  if (cleaned.startsWith("56")) {
    if (cleaned.length <= 2) return "+56";
    if (cleaned.length <= 3) return `+56 ${cleaned.slice(2)}`;
    return `+56 ${cleaned.slice(2, 3)} ${cleaned.slice(3, 11)}`;
  }

  if (cleaned.startsWith("9")) {
    if (cleaned.length === 1) return "+56 9";
    return `+56 9 ${cleaned.slice(1, 9)}`;
  }

  return `+56 ${cleaned.slice(0, 1)} ${cleaned.slice(1, 9)}`;
};

export const NewCustomer = () => {
  const [customer, setCustomer] = useState(DEFAULT_CUSTOMER);
  const [snack, setSnackbar] = useState(DEFAULT_SNACK);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = ({ target: { name, value } }) => {
    let formattedValue = value;

    if (name === "customer_id") {
      formattedValue = formatRut(value);
    } else if (name === "phone") {
      formattedValue = formatPhone(value);
    }

    setCustomer({
      ...customer,
      [name]: formattedValue,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!customer.name || !customer.customer_id || !customer.phone) {
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Nombre, RUT y teléfono son obligatorios",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      return;
    }

    try {
      setLoading(true);
      const result = await createCustomer(customer);

      if (result.success) {
        setSnackbar({
          visible: true,
          severity: "success",
          message: "Cliente creado exitosamente",
        });
        setTimeout(() => {
          setSnackbar(DEFAULT_SNACK);
          navigate("/dashboard/customers");
        }, 2000);
      } else {
        setSnackbar({
          visible: true,
          severity: "error",
          message: result.message || "Error al crear cliente",
        });
        setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      setSnackbar({
        visible: true,
        severity: "error",
        message: "Error al crear cliente",
      });
      setTimeout(() => setSnackbar(DEFAULT_SNACK), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-sidebar-bg">
      <Sidebar />
      <div className="flex-1 flex flex-col p-4">
        <Wrapper>
          <div className="flex-1 p-6 lg:p-8">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <button
                  onClick={() => navigate("/dashboard/customers")}
                  className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-4"
                >
                  <ArrowBackIcon fontSize="small" />
                  Volver a Clientes
                </button>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
                    <PersonAddIcon className="text-white" fontSize="small" />
                  </div>
                  <h1 className="text-3xl font-bold text-white">Nuevo Cliente</h1>
                </div>
                <p className="text-gray-400 text-sm mt-1">
                  Registra un nuevo cliente en el sistema
                </p>
              </div>

              <form onSubmit={handleSubmit} className="bg-sidebar-bg rounded-xl border border-sidebar-accent p-6 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    icon={BadgeIcon}
                    label="Nombre completo"
                    name="name"
                    value={customer.name}
                    onChange={handleChange}
                    placeholder="Juan Pérez"
                    required
                  />

                  <InputField
                    icon={BadgeIcon}
                    label="RUT"
                    name="customer_id"
                    value={customer.customer_id}
                    onChange={handleChange}
                    placeholder="12.345.678-9"
                    required
                    helperText="Formato: 12.345.678-9"
                  />

                  <InputField
                    icon={PhoneIcon}
                    label="Teléfono"
                    name="phone"
                    value={customer.phone}
                    onChange={handleChange}
                    placeholder="+56 9 1234 5678"
                    required
                    helperText="Formato: +56 9 XXXX XXXX"
                  />

                  <InputField
                    icon={EmailIcon}
                    label="Email"
                    name="email"
                    type="email"
                    value={customer.email}
                    onChange={handleChange}
                    placeholder="cliente@ejemplo.com"
                    helperText="Opcional"
                  />
                </div>

                <InputField
                  icon={HomeIcon}
                  label="Dirección"
                  name="address"
                  value={customer.address}
                  onChange={handleChange}
                  placeholder="Calle Falsa 123, Santiago"
                  helperText="Opcional"
                />

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/customers")}
                    className="flex-1 px-6 py-3 bg-sidebar-accent hover:bg-sidebar-hover text-white font-medium rounded-lg transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creando...
                      </>
                    ) : (
                      <>
                        <PersonAddIcon />
                        Crear Cliente
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </Wrapper>
      </div>

      <Snackbar
        severity={snack.severity}
        message={snack.message}
        visible={snack.visible}
      />
    </div>
  );
};

const InputField = ({
  icon: Icon,
  label,
  name,
  value,
  onChange,
  placeholder,
  type = "text",
  required = false,
  helperText = null,
}) => {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-400 mb-2">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <Icon className="text-gray-500" sx={{ fontSize: 20 }} />
        </div>
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
          className="w-full pl-11 pr-4 py-3 bg-white rounded-lg text-black placeholder-gray-500 focus:outline-none transition-all border-2 border-sidebar-accent focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
        />
      </div>
      {helperText && (
        <p className="text-xs text-gray-500 mt-1.5 ml-1">{helperText}</p>
      )}
    </div>
  );
};
